import React, { useState, useEffect } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import type { BackgroundPattern } from "../../contexts/ThemeContext";

const Background: React.FC = () => {
  const { currentTheme, backgroundPattern, isAnimated } = useTheme();
  const [colors, setColors] = useState(currentTheme.colors);

  useEffect(() => {
    setColors(currentTheme.colors);
  }, [currentTheme]);

  const renderPattern = () => {
    switch (backgroundPattern) {
      case "grid":
        return (
          <div
            className="absolute inset-0 transition-all duration-[4000ms] ease-in-out opacity-80"
            style={{
              backgroundImage: `linear-gradient(${colors.background.base} 2px, transparent 2px),
                                linear-gradient(to right, ${colors.background.base} 2px, transparent 2px)`,
              backgroundSize: "40px 40px",
              animation: isAnimated ? "gridShift 14s ease-in-out infinite alternate" : "none",
            }}
          />
        );
      case "dots":
        return (
          <div
            className="absolute inset-0 transition-all duration-[4000ms] ease-in-out opacity-70"
            style={{
              backgroundImage: `radial-gradient(circle, ${colors.background.base} 2px, transparent 2px)`,
              backgroundSize: "30px 30px",
              animation: isAnimated ? "gridShift 14s ease-in-out infinite alternate" : "none",
            }}
          />
        );
      case "lines":
        return (
          <div
            className="absolute inset-0 transition-all duration-[4000ms] ease-in-out opacity-75"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 30px, ${colors.background.base} 30px, ${colors.background.base} 32px)`,
              animation: isAnimated ? "gridShift 14s ease-in-out infinite alternate" : "none",
            }}
          />
        );
      case "waves":
        return (
          <div
            className="absolute inset-0 opacity-50 transition-all duration-[4000ms] ease-in-out"
            style={{
              backgroundImage: `repeating-radial-gradient(circle at 0 0, transparent 0, ${colors.background.base} 35px, transparent 70px)`,
              animation: isAnimated ? "wave 16s ease-in-out infinite" : "none",
            }}
          />
        );
      case "circles":
        return (
          <div
            className="absolute inset-0 transition-all duration-[4000ms] ease-in-out opacity-65"
            style={{
              backgroundImage: `radial-gradient(circle, transparent 18%, ${colors.background.base} 18%, ${colors.background.base} 22%, transparent 22%), 
                                radial-gradient(circle, transparent 18%, ${colors.background.base} 18%, ${colors.background.base} 22%, transparent 22%)`,
              backgroundSize: "50px 50px",
              backgroundPosition: "0 0, 25px 25px",
              animation: isAnimated ? "gridShift 14s ease-in-out infinite alternate" : "none",
            }}
          />
        );
      case "none":
        return null;
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 overflow-hidden pointer-events-none transition-colors duration-1000"
      style={{
        zIndex: -9999,
        backgroundColor: colors.background.base,
      }}>
      <div
        className="absolute inset-0 transition-all duration-[8000ms] ease-in-out"
        style={{
          background: `linear-gradient(to bottom right, ${colors.background.base}, ${colors.background.via}, ${colors.background.to})`,
          backgroundImage: `radial-gradient(circle at 50% 50%, ${colors.primary}1a 0%, transparent 80%),
                            linear-gradient(to bottom right, ${colors.primary}1a, transparent)`,
          animation: isAnimated ? "gradientShift 12s ease-in-out infinite alternate" : "none",
        }}>
        <div
          className="absolute w-96 h-96 rounded-full blur-3xl transition-colors duration-1000"
          style={{
            top: "20%",
            left: "10%",
            backgroundColor: colors.orbs.orb1,
            animation: isAnimated ? "float1 15s ease-in-out infinite" : "none",
          }}
        />

        <div
          className="absolute w-72 h-72 rounded-full blur-3xl transition-colors duration-1000"
          style={{
            top: "60%",
            right: "15%",
            backgroundColor: colors.orbs.orb2,
            animation: isAnimated ? "float2 18s ease-in-out infinite reverse" : "none",
          }}
        />

        <div
          className="absolute w-64 h-64 rounded-full blur-3xl transition-colors duration-1000"
          style={{
            bottom: "30%",
            left: "60%",
            backgroundColor: colors.orbs.orb3,
            animation: isAnimated ? "float3 20s ease-in-out infinite" : "none",
          }}
        />

        <div
          className="absolute inset-0 transition-opacity duration-[6000ms] ease-in-out"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 90%, rgba(0, 0, 0, 0.9) 100%),
                              linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 90%, rgba(0, 0, 0, 0.4) 100%)`,
            backgroundSize: "100% 100%, 100% 100%",
            animation: isAnimated ? "overlayPulse 10s ease-in-out infinite alternate" : "none",
          }}
        />

        {renderPattern()}

        <div
          className="absolute inset-0 opacity-20 transition-colors duration-1000"
          style={{
            backgroundImage: `radial-gradient(ellipse at center, ${colors.primary}1a 0%, transparent 70%)`,
            animation: isAnimated ? "wave 16s ease-in-out infinite" : "none",
          }}
        />
      </div>
    </div>
  );
};

export default Background;
